import React, { useState, useEffect } from 'react';
import networkService from '../services/networkApi';
import { 
  BuildingOfficeIcon,
  UsersIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ClipboardDocumentListIcon,
  XMarkIcon,
  ChevronRightIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

// Componenti della rete piramidale
import CostruttoreRete from '../components/network/CostruttoreRete';
import GestoreNodi from '../components/network/GestoreNodi';
import SpostaNodoModal from '../components/network/SpostaNodoModal';
import GestoreTemplatePreventivi from '../components/network/GestoreTemplatePreventivi';
import LicenseStatusWidget from '../components/network/LicenseStatusWidget';

const ReteMultilivelloPage = () => {
  // Recupera utente corrente da localStorage
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('user'));
  } catch (e) {
    user = null;
  }
  const ruoloUtente = (user?.role || user?.ruolo || '').toLowerCase();
  const isAdmin = ruoloUtente === 'admin';
  // Stato principale della rete
  const [reteCorrente, setReteCorrente] = useState(null);
  const [nodoSelezionato, setNodoSelezionato] = useState(null);
  
  // Stati UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalNodo, setModalNodo] = useState({
    aperto: false,
    tipo: 'create',
    nodo: null,
    parent: null
  });
  // Stato per il modale di spostamento nodo
  const [modalSposta, setModalSposta] = useState({ aperto: false, nodo: null });

  // Nuovo stato per gestione branding
  const [showBrandingManager, setShowBrandingManager] = useState(false);
  const [agenziaSelezionataBranding, setAgenziaSelezionataBranding] = useState(null);

  // Stato per notifiche
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success', // 'success', 'error', 'warning', 'info'
    timeout: null
  });

  // Configurazione tipi nodo
  const tipiNodo = [
    {
      id: 'agenzia_primaria',
      nome: 'Agenzia Primaria',
      icona: BuildingOfficeIcon,
      colore: 'blue',
      descrizione: 'Nodo radice della rete, riceve % sulla produzione totale',
      limiteMassimo: 1 // Solo una per rete
    },
    {
      id: 'team_leader',
      nome: 'Team Leader',
      icona: UsersIcon,
      colore: 'green',
      descrizione: 'Coordinatore intermedio, riceve % sul margine del livello superiore',
      limiteMassimo: null
    },
    {
      id: 'agente',
      nome: 'Agente',
      icona: UsersIcon,
      colore: 'purple',
      descrizione: 'Nodo terminale o intermedio, riceve % sul margine residuo',
      limiteMassimo: null
    },
    {
      id: 'sito_vetrina',
      nome: 'Sito Vetrina',
      icona: GlobeAltIcon,
      colore: 'yellow',
      descrizione: 'Nodo di vetrina, non riceve %',
      limiteMassimo: null
    }
  ];

  // Carica rete all'avvio
  useEffect(() => {
    caricaReteCorrente();
  }, []);

  // Funzione per mostrare una notifica
  const showNotification = (message, type = 'success', duration = 3000) => {
    // Clear any existing timeout
    if (notification.timeout) {
      clearTimeout(notification.timeout);
    }
    
    // Set the notification
    setNotification({
      show: true,
      message,
      type,
      timeout: setTimeout(() => {
        setNotification(prev => ({ ...prev, show: false }));
      }, duration)
    });
  };

  const caricaReteCorrente = async () => {
    try {
      setLoading(true);
      const response = await networkService.getReteCorrente();
      setReteCorrente(response.data);
    } catch (error) {
      console.error('Errore caricamento rete:', error);
      if (error.response?.status === 404) {
        setReteCorrente({ nodi: [] });
      } else {
        setError('Errore nel caricamento della rete');
        showNotification('Errore nel caricamento della rete', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const aggiungiNodo = async (datiNodo, parentId = null) => {
    try {
      setLoading(true);
      
      // Per i siti vetrina, se non c'è un parentId, usa l'agenzia primaria come default
      if (datiNodo.tipo === 'sito_vetrina' && !parentId && reteCorrente?.nodi?.length > 0) {
        // Trova l'agenzia primaria (primo nodo con parentId null)
        const agenziaPrimaria = reteCorrente.nodi.find(n => n.parentId === null);
        if (agenziaPrimaria) {
          parentId = agenziaPrimaria.id;
          showNotification(`Sito vetrina creato sotto l'agenzia primaria: ${agenziaPrimaria.nome}`, 'info');
        }
      }
      
      // Determina il gruppoId in base al parent o usa quello di default
      let gruppoId = 1; // Default
      
      if (parentId && reteCorrente?.nodi) {
        const parentNodo = reteCorrente.nodi.find(n => n.id === parentId);
        if (parentNodo) {
          gruppoId = parentNodo.gruppoId;
          }
      } else if (reteCorrente?.nodi?.length > 0) {
        // Se non c'è parent, usa il gruppo del primo nodo (agenzia primaria)
        gruppoId = reteCorrente.nodi[0].gruppoId;
      }
      
      // Mappa i dati per il backend
      const datiBackend = {
        nome: datiNodo.nome,
        tipo: datiNodo.tipo, // Backend si aspetta 'tipo' non 'tipoNodo'
        utenteId: datiNodo.utenteId, // Backend si aspetta 'utenteId' non 'userId'
        descrizione: datiNodo.descrizione,
        provvigionePercentuale: datiNodo.provvigionePercentuale || null,
        provvigioneFissa: datiNodo.provvigioneFissa || null,
        parentId,
        gruppoId // Usa il gruppoId dinamico
      };

      // Campi specifici per sito_vetrina
      if (datiNodo.tipo === 'sito_vetrina') {
        datiBackend.fonteId = datiNodo.fonteId;
        datiBackend.dettaglioProvenienzaId = datiNodo.dettaglioProvenienzaId;
        datiBackend.urlSito = datiNodo.urlSito;
        datiBackend.apiKey = datiNodo.apiKey;
      }
      
      const response = await networkService.postNodi(datiBackend);
      
      if (response.success) {
        await caricaReteCorrente(); // Ricarica la rete aggiornata
        setModalNodo({ aperto: false, tipo: 'create', nodo: null, parent: null });
        showNotification('Nodo aggiunto con successo!', 'success');
      }
    } catch (error) {
      console.error('Errore aggiunta nodo:', error);
      const errorMessage = error.response?.data?.message || 'Errore nell\'aggiunta del nodo';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const rimuoviNodo = async (nodoId) => {
    if (!window.confirm('Sei sicuro di voler rimuovere questo nodo? Tutti i nodi sottostanti verranno rimossi.')) {
      return;
    }

    try {
      setLoading(true);
      await networkService.deleteNodi(nodoId);
      await caricaReteCorrente();
      setNodoSelezionato(null);
      showNotification('Nodo rimosso con successo!', 'success');
    } catch (error) {
      console.error('Errore rimozione nodo:', error);
      
      // Gestione specifica per agenzie primarie
      if (error.response?.data?.error?.includes('Agenzia Primaria')) {
        const errorMessage = 'Non è possibile rimuovere un\'Agenzia Primaria. Le agenzie primarie sono i nodi radice della rete e devono essere mantenute per preservare la struttura gerarchica.';
        setError(errorMessage);
        showNotification(errorMessage, 'error');
      } else {
        const errorMessage = error.response?.data?.message || 'Errore nella rimozione del nodo';
        setError(errorMessage);
        showNotification(errorMessage, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const modificaNodo = async (datiNodo) => {
    try {
      setLoading(true);
      await networkService.aggiornaNodo(datiNodo.id, datiNodo);
      await caricaReteCorrente();
      setModalNodo({ aperto: false, tipo: 'create', nodo: null, parent: null });
      showNotification('Nodo modificato con successo!', 'success');
    } catch (error) {
      console.error('Errore modifica nodo:', error);
      const errorMessage = error.response?.data?.message || 'Errore nella modifica del nodo';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Spostamento di un nodo sotto un nuovo responsabile
  const spostaNodo = async (nuovoParentId, gestioneFigli) => {
    try {
      setLoading(true);
      await networkService.spostaNodo(modalSposta.nodo.id, nuovoParentId, gestioneFigli);
      await caricaReteCorrente();
      setModalSposta({ aperto: false, nodo: null });
      showNotification('Nodo spostato con successo!', 'success');
    } catch (error) {
      console.error('Errore spostamento nodo:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Errore nello spostamento del nodo';
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Cambio grado rapido (agente <-> team leader)
  const cambiaGrado = async (nodo, nuovoTipo) => {
    const etichetta = tipiNodo.find(t => t.id === nuovoTipo)?.nome || nuovoTipo;
    if (!window.confirm(`Vuoi cambiare il grado di questo nodo in "${etichetta}"?`)) return;
    try {
      setLoading(true);
      await networkService.aggiornaNodo(nodo.id, { tipo: nuovoTipo });
      await caricaReteCorrente();
      showNotification(`Grado aggiornato a "${etichetta}"!`, 'success');
    } catch (error) {
      console.error('Errore cambio grado:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Errore nel cambio di grado';
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const getTipoNodo = (tipo) => {
    return tipiNodo.find(t => t.id === tipo) || tipiNodo[2];
  };

  // Funzione per aprire gestore branding per agenzia specifica
  const apriGestoreBranding = (agenzia) => {
    setAgenziaSelezionataBranding(agenzia);
    setShowBrandingManager(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notifiche */}
      {notification.show && (
        <div 
          className={`fixed top-4 right-4 z-50 shadow-lg rounded-lg px-4 py-3 border-l-4 flex items-start transition-all duration-300 animate-slide-in-right max-w-md ${
            notification.type === 'success' ? 'bg-green-50 text-green-800 border-green-500' :
            notification.type === 'error' ? 'bg-red-50 text-red-800 border-red-500' :
            notification.type === 'warning' ? 'bg-yellow-50 text-yellow-800 border-yellow-500' :
            'bg-blue-50 text-blue-800 border-blue-500'
          }`}
        >
          <div className="mr-3 flex-shrink-0">
            {notification.type === 'success' && (
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
            )}
            {notification.type === 'error' && (
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
            )}
            {notification.type === 'warning' && (
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
            )}
            {notification.type === 'info' && (
              <InformationCircleIcon className="h-5 w-5 text-blue-500" />
            )}
          </div>
          
          <div className="flex-1">
            <p className="font-medium">{notification.message}</p>
          </div>
          
          <button 
            className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600"
            onClick={() => setNotification(prev => ({ ...prev, show: false }))}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Rete Multilivello</h1>
              <p className="mt-1 text-sm text-gray-500">
                {reteCorrente?.nodi?.length || 0} nodi nella rete
              </p>
            </div>
            {/* Bottone Aggiungi Nodo: SOLO admin */}
            {isAdmin && (
            <button
              onClick={() => setModalNodo({ 
                aperto: true, 
                tipo: 'create', 
                nodo: null, 
                parent: nodoSelezionato 
              })}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Aggiungi Nodo
            </button>
            )}
          </div>
        </div>

        {/* Widget Stato Licenze */}
        <div className="mb-6">
          <LicenseStatusWidget 
            userRole={ruoloUtente} 
            userId={user?.id}
          />
        </div>

        {/* Alert errori */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-red-800 font-medium">Errore</h3>
                <p className="text-red-600 text-sm mt-1">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Lista Rete */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-500">Caricamento rete...</p>
            </div>
          ) : reteCorrente && reteCorrente.nodi?.length > 0 ? (
            <CostruttoreRete
              rete={reteCorrente}
              nodoSelezionato={nodoSelezionato}
              onNodoSelezionato={setNodoSelezionato}
              onAggiungiNodo={(parent) => {
                if (isAdmin) {
                  setModalNodo({ 
                aperto: true, 
                tipo: 'create', 
                nodo: null, 
                parent 
                  });
                }
              }}
              onModificaNodo={(nodo) => setModalNodo({ 
                aperto: true, 
                tipo: 'edit', 
                nodo, 
                parent: null 
              })}
              onRimuoviNodo={rimuoviNodo}
              onSpostaNodo={(nodo) => setModalSposta({ aperto: true, nodo })}
              onCambiaGrado={cambiaGrado}
              onGestisciTemplate={apriGestoreBranding}
              tipiNodo={tipiNodo}
              user={user}
            />
          ) : (
            <div className="p-12 text-center">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <BuildingOfficeIcon className="h-12 w-12" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Nessun nodo nella rete</h3>
              <p className="mt-2 text-sm text-gray-500">
                Inizia aggiungendo un'Agenzia Primaria come nodo radice per costruire la tua rete.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setModalNodo({ 
                    aperto: true, 
                    tipo: 'create', 
                    nodo: null, 
                    parent: null 
                  })}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Crea Prima Agenzia
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Creazione/Modifica Nodo */}
      {modalNodo.aperto && (
        <GestoreNodi
          isOpen={modalNodo.aperto}
          onClose={() => setModalNodo({ aperto: false, tipo: 'create', nodo: null, parent: null })}
          onSave={modalNodo.tipo === 'create' ? aggiungiNodo : modificaNodo}
          tipo={modalNodo.tipo}
          nodoIniziale={modalNodo.nodo}
          parentNodo={modalNodo.parent}
          tipiNodo={tipiNodo}
        />
      )}

      {/* Modal Spostamento Nodo */}
      {modalSposta.aperto && modalSposta.nodo && (
        <SpostaNodoModal
          nodo={modalSposta.nodo}
          nodi={reteCorrente?.nodi || []}
          loading={loading}
          onClose={() => setModalSposta({ aperto: false, nodo: null })}
          onConferma={spostaNodo}
        />
      )}

      {/* Modal Gestore Branding & Template */}
      {showBrandingManager && agenziaSelezionataBranding && (
        <GestoreTemplatePreventivi
          isOpen={showBrandingManager}
          onClose={() => {
            setShowBrandingManager(false);
            setAgenziaSelezionataBranding(null);
          }}
          agenzia={agenziaSelezionataBranding}
        />
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              <span className="text-gray-900 font-medium">Elaborazione...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReteMultilivelloPage; 